/**
 * Hook to submit feedback for multiple moderation cases in a single batch request.
 *
 * Follows the same transformErrors() + DomainError[] pattern as useSubmitCaseFeedback,
 * but sends an array of CaseFeedbackInput items via the submitBatchCaseFeedback mutation.
 */

import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import type { SubmitBatchCaseFeedbackMutation } from '@core/graphql/types';
import { useState } from 'react';

import type { CaseFeedbackInput } from '../domain/CaseFeedback';
import { SUBMIT_BATCH_CASE_FEEDBACK } from '../graphql';

/**
 * Hook to submit moderation case feedback in batch.
 *
 * @returns submitBatchFeedback function, loading state, errors, and reset function
 *
 * @example
 * ```tsx
 * const { submitBatchFeedback, loading, errors } = useSubmitBatchCaseFeedback();
 * const result = await submitBatchFeedback(feedbackItems);
 * ```
 */
export function useSubmitBatchCaseFeedback() {
  const [submitMutation, { loading }] = useMutation<SubmitBatchCaseFeedbackMutation>(SUBMIT_BATCH_CASE_FEEDBACK);
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const submitBatchFeedback = async (items: CaseFeedbackInput[]): Promise<SubmitBatchCaseFeedbackMutation['submitBatchCaseFeedback'] | null> => {
    try {
      setErrors(null);
      const { data } = await submitMutation({
        variables: { input: { items } },
      });

      return data?.submitBatchCaseFeedback || null;
    } catch (error) {
      setErrors(transformErrors(error));

      return null;
    }
  };

  const resetErrors = () => setErrors(null);

  return {
    submitBatchFeedback,
    loading,
    errors,
    resetErrors,
  };
}
